import React, { FC, useEffect, useState } from "react";
import * as Highcharts from "highcharts";
import highchartsMore from "highcharts/highcharts-more";
import { applyTipWith, wideFontSize24 } from "@utils/utils";
import { HCDateLabelsStyle } from "@utils/highchart/labels";
import BaseChartTooltipWithHeader, {
  HeaderTypes
} from "@modules/dashboards/dashboardTooltips/TooltipVariants/BaseChartTooltipWithHeader/BaseChartTooltipWithHeader";
import { useTypedSelector } from "@hooks/useTypedSelector";
import { IPhasesElement, IPhasesOptions } from "../../../../../store/reducers/dashboard/phases/types";
import LinearChart from "../../../../../components/modules/dashboards/сharts/defaultCharts/linearChart/LinearChart";
import { getYear } from "date-fns";
import Scrollbar from "@utils/highchart/Scrollbar";

highchartsMore(Highcharts);

interface GrowthPhasesByMonthChart {
  stageRipeScoutingDash: IPhasesElement[];
  apiCallback?: Highcharts.ChartCallbackFunction
}

const GrowthPhasesByMonthChart: FC<GrowthPhasesByMonthChart> = (props) => {
  let {stageRipeScoutingDash} = props;

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({ title: { text: '' } });

  const { selectedDate } = useTypedSelector(state => state.selectedDate)

  useEffect(() => {
    const year = getYear(selectedDate || new Date())
    const minChartValue = new Date(Date.UTC(year, 0)).getTime();
    const maxChartValue = new Date(Date.UTC(year, 11, 31)).getTime();

      const defaultOptions: Highcharts.Options = {
        chart: {
          height: 300,
          type: "columnrange",
          inverted: true,
          spacing: [0, 0, 0, 0],
          zooming: {
            type: 'y',
            // @ts-ignore
            mouseWheel: {
              enabled: false
            },
            resetButton: {
              theme: {
                zIndex: 99
              }
            }
          }
        },
        title: {
          text: "",
        },
        xAxis: {
          categories: stageRipeScoutingDash.map(ripe => ripe.title),
          visible: false
        },
        yAxis: {
          min: minChartValue,
          max: maxChartValue,
          tickmarkPlacement: 'between',
          showEmpty: true,
          type: 'datetime',
          opposite: true,
          dateTimeLabelFormats: {
            month: '%b',
          },
          scrollbar: Scrollbar({ margin: 20 }),
          labels: {
            autoRotation: undefined,
            style: HCDateLabelsStyle({
              fontSize: wideFontSize24(),
              textAlign: 'center'
            }),
            x: 35,
            overflow: 'allow',
          },
          minTickInterval: 3600 * 24 * 1000,
          minRange: 3600 * 24 * 1000,
          title: { text: undefined }
        },
        tooltip: applyTipWith(ctx => {
          const phasesOptions = ctx.point.series.options as IPhasesOptions;
          return <BaseChartTooltipWithHeader
              header={{ type: HeaderTypes.phases, title: phasesOptions.name }}
              elements={
                phasesOptions.cropPeriods.map(period => ({
                  color: period.color,
                  name: period.name,
                  id: phasesOptions.id + `-${period.name}-` + period.start,
                  dateStart: period.start,
                  dateEnd: period.end
                }))
              }
              width={250}
          />
        }),
        plotOptions: {
          columnrange: {
            tooltip: {
              followPointer: true,
              followTouchMove: true
            },
            groupPadding: 1,
            pointPadding: 0.1,
            borderWidth: 0,
            borderRadius: 6,
            dataLabels: {
              enabled: false
            }
          }
        },
        credits: {
          enabled: false
        },
        legend: {
          enabled: false,
        },
        series: stageRipeScoutingDash.map((ripe, index) => {
          const { start, end = maxChartValue, color } = ripe;
          return {
            id: String(ripe.id),
            type: "columnrange",
            ha: ripe.sum,
            name: ripe.title,
            color,
            data: [[index, start, end]],
            pointWidth: 25,
            cropPeriods: ripe.cropPeriods
          };
        }),
      };
      setChartOptions(defaultOptions);
  }, [stageRipeScoutingDash, selectedDate]);

  return <LinearChart
      callback={props.apiCallback}
      containerProps={{ style: { height: 300, width: "100%", overflow: 'hidden' } }}
      options={chartOptions}
      isStock
      isHcMore
      zoomAxis={'yAxis'}
  />;
};

export default GrowthPhasesByMonthChart;
